/**
 * Following a World as it changes: a watch socket opened with a ticket,
 * and polling when a socket cannot be had.
 *
 * Events are only a nudge; the console re-reads what it shows when told.
 * So polling can stand in for the socket by watching the journal head, and
 * a missed event costs nothing but a moment's staleness.
 */

import type { Api } from './api';

export type Mode = 'connecting' | 'live' | 'polling' | 'stopped';

/** What the console hears: an event from the socket, or a head that moved under polling. */
export interface Heard {
  readonly type: string;
  readonly head?: string | null;
  readonly data?: Record<string, unknown>;
}

export interface FollowOptions {
  readonly onEvent: (event: Heard) => void;
  readonly onMode?: (mode: Mode, why?: string) => void;
  /** How the socket is made; a test hands in a fake. */
  readonly socket?: (url: string) => WebSocket;
  readonly pollMs?: number;
  /** Tries at a socket before settling for polling. */
  readonly tries?: number;
  /** The first wait before reopening a dropped socket; it doubles with each failure. */
  readonly retryMs?: number;
}

/** Follow a World until the returned function is called. */
export const follow = (api: Api, world: string, options: FollowOptions): (() => void) => {
  const makeSocket = options.socket ?? ((url: string) => new WebSocket(url));
  const pollMs = options.pollMs ?? 4000;
  const tries = options.tries ?? 3;
  const retryMs = options.retryMs ?? 1000;
  let stopped = false;
  let socket: WebSocket | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pinger: ReturnType<typeof setInterval> | null = null;
  let failures = 0;
  let head: string | null = null;

  const mode = (m: Mode, why?: string) => { if (!stopped || m === 'stopped') options.onMode?.(m, why); };

  const poll = async () => {
    if (stopped) return;
    const world_ = await api.world(world);
    if (stopped) return;
    if (world_.ok) {
      if (head !== null && world_.value.head !== head) options.onEvent({ type: 'commit', head: world_.value.head });
      head = world_.value.head;
    }
    timer = setTimeout(poll, pollMs);
  };

  const fallBack = (why: string) => {
    if (stopped) return;
    mode('polling', why);
    void poll();
  };

  const connect = async () => {
    if (stopped) return;
    mode('connecting');
    const ticket = await api.watchTicket(world);
    if (stopped) return;
    if (!ticket.ok) {
      fallBack(ticket.problem.detail);
      return;
    }
    let opened = false;
    try {
      socket = makeSocket(api.watchUrl(world, ticket.value.ticket));
    } catch (error) {
      fallBack(String(error));
      return;
    }
    socket.onmessage = (m) => {
      if (m.data === 'pong') return;
      let event: Heard;
      try {
        event = JSON.parse(String(m.data)) as Heard;
      } catch {
        return;
      }
      if (event.type === 'hello') {
        opened = true;
        failures = 0;
        mode('live');
        return;
      }
      options.onEvent(event);
    };
    // A socket left quiet may be dropped by a proxy; the World answers "ping" with "pong".
    pinger = setInterval(() => { if (socket?.readyState === 1) socket.send('ping'); }, 30_000);
    socket.onclose = () => {
      if (pinger) clearInterval(pinger);
      pinger = null;
      socket = null;
      if (stopped) return;
      failures += 1;
      if (failures >= tries) {
        fallBack(opened ? 'the watch kept closing' : 'the watch could not be opened');
        return;
      }
      // A ticket is single use, so every reconnect asks for a new one.
      timer = setTimeout(connect, retryMs * 2 ** (failures - 1));
    };
  };

  void connect();

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    if (pinger) clearInterval(pinger);
    socket?.close();
    mode('stopped');
  };
};
